/**
 * @author Housefolios
 * @createTime 2025-01-27 10:00:00
 * @modifiedBy AI Assistant
 * @modifiedTime 2025-01-27 10:00:00
 * @description Base component for strategy pages with common functionality
 */

import React, { useEffect, useState } from 'react'
import {
  strategyFlexGrowSx,
  strategyFontSemiboldSx,
  strategyOrderCloseIconSx,
  strategyOrderDrawerCloseWrapSx,
  strategyOrderDrawerPaperSx,
  strategyOrderFlexSpacerSx,
  strategyOrderFormWhiteSx,
  strategyOrderResetBtnWrapSx,
  strategyOrderSaveBtnSx,
  strategyOrderSaveLabelSx,
} from '@/housefolios-components/PropertyContainer/CenterPage/Common/strategyDashboardSx'
import { useMutation } from '@apollo/client/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  map,
  filter,
  sortBy,
  findIndex,
  merge,
  cloneDeep,
  isEmpty,
  slice,
  compact,
  concat,
  uniq,
  difference,
} from '@/utils/lodash'

import {
  Grid,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormGroup,
  FormControlLabel,
  Tooltip,
  Typography,
  Drawer,
  IconButton,
  FormControl,
} from '@mui/material'

import {
  arrayMove,
  SortableItem,
  SortableList,
} from '@/components/sortable'

import { Close } from '@mui/icons-material'
import { enqueueSnackbar } from 'notistack'

import { UPDATE_PROPERTY } from '@/housefolios-components/PropertyContainer/mutations'
import { UPDATE_ORGANIZATION } from '@/housefolios-components/OrganizationSettings/mutations'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { DEFAULT_PREFERENCES } from '@/housefolios-components/OrganizationSettings/OrganizationPreferences'

/**
 * Base component for strategy pages
 * @param {Object} props - Component props
 * @param {Array} props.cards - Array of card configurations
 * @param {string} props.strategyName - Name of the strategy
 * @param {string} props.strategyKey - Key used for strategy order
 * @param {Object} props.property - Property object
 * @param {Object} props.activeOrg - Active organization
 * @param {boolean} props.canDrag - Whether cards can be dragged/reordered
 * @param {boolean} props.publicDashboard - Whether this is a public dashboard
 * @param {React.ReactNode} props.publicImageCard - Public image card component
 * @param {Object} props.session - Session object
 * @param {Function} props.renderInputFields - Function to render input fields
 */
function StrategyPageBase({
  cards,
  strategyName,
  strategyKey,
  property,
  activeOrg,
  canDrag = false,
  publicDashboard = false,
  publicImageCard = null,
  session,
  renderInputFields,
  ...otherProps
}) {
  const safeCards = cards || []
  const safeOrg = activeOrg || {}
  const [order, setOrder] = useState([])
  const [orderChanged, setOrderChanged] = useState(false)
  const [orderDefault, setOrderDefault] = useState(false)
  const [loader, setLoader] = useState(false)

  const [updateProperty] = useMutation(UPDATE_PROPERTY)
  const [updateOrganization] = useMutation(UPDATE_ORGANIZATION)

  // Get default order from preferences
  const getOrderDefault = () => {
    const orderKey = `${strategyKey}Order`
    const storedValues = !isEmpty(property?.[orderKey])
      ? property[orderKey]
      : merge(
          cloneDeep(DEFAULT_PREFERENCES),
          safeOrg.memberPreferences,
          safeOrg.preferences,
          session?.me && !publicDashboard
            ? session.me.preferences
            : {},
        )[orderKey] || []

    const newCards = difference(
      map(
        filter(safeCards, (card) => !card.hide),
        (card) => card.name,
      ),
      storedValues,
    )
    const deactiveIndex = findIndex(
      storedValues,
      (value) => value === 'deactive',
    )
    return uniq(
      concat(
        slice(storedValues, 0, deactiveIndex),
        newCards,
        slice(storedValues, deactiveIndex),
      ),
    )
  }

  useEffect(() => {
    setOrder(getOrderDefault())
  }, [safeOrg.member, strategyName])

  // Sort cards based on order
  const sortedCards = sortBy(
    filter(safeCards, (card) => !card.hide),
    [
      function (o) {
        return findIndex(order, (value) => value === o.name)
      },
    ],
  )

  // Handle card reordering (ids are stable card names, not slot indexes)
  const onSortEnd = ({ oldIndex, newIndex  }) => {
    if (oldIndex === newIndex) return

    const offset = publicImageCard ? 1 : 0
    const from = oldIndex - offset
    const to = newIndex - offset
    // Public image is visual-only; ignore drags that only involve it.
    if (from < 0 || to < 0) return

    const visibleNames = map(sortedCards, (card) => card.name)
    const reorderedVisible = arrayMove(visibleNames, from, to)
    let visibleIndex = 0
    const newOrder = map(order, (name) =>
      visibleNames.includes(name)
        ? reorderedVisible[visibleIndex++]
        : name,
    )
    // Append any visible cards missing from order (shouldn't happen, but safe)
    const missing = reorderedVisible.filter(
      (name) => !newOrder.includes(name),
    )
    setOrder(missing.length ? concat(newOrder, missing) : newOrder)
    setOrderChanged(true)
  }

  // Reset order to default
  const resetOrder = () => {
    setOrder(getOrderDefault())
    setOrderChanged(false)
  }

  // Submit order changes
  const submitOrder = () => {
    setLoader(true)
    const orderKey = `${strategyKey}Order`
    const preferences = merge(
      cloneDeep(DEFAULT_PREFERENCES),
      safeOrg.memberPreferences,
      safeOrg.preferences,
    )

    updateProperty({
      refetchQueries: [
        {
          query: GET_ME,
        },
      ],
      variables: {
        id: property._id,
        propertyInput: {
          [orderKey]: order,
        },
      },
    })
      .then(() => {
        enqueueSnackbar(`Layout saved`, {
          variant: 'success',
          autoHideDuration: 3000,
        })
        if (orderDefault) {
          updateOrganization({
            refetchQueries: [
              {
                query: GET_ME,
              },
            ],
            variables: {
              id: safeOrg._id,
              organizationInput: {
                preferences: {
                  ...preferences,
                  [orderKey]: order,
                },
              },
            },
          })
            .then(() => {
              enqueueSnackbar(
                `Default ${strategyName} layout saved`,
                {
                  variant: 'success',
                  autoHideDuration: 3000,
                },
              )
              setOrderChanged(false)
              setLoader(false)
            })
            .catch((error) => {
              enqueueSnackbar(
                `Failed to save default ${strategyName} layout`,
                {
                  variant: 'error',
                  autoHideDuration: 3000,
                },
              )
            })
        } else {
          setOrderChanged(false)
          setLoader(false)
        }
      })
      .catch((error) => {
        enqueueSnackbar(`Failed to save layout`, {
          variant: 'error',
          autoHideDuration: 3000,
        })
      })
  }

  const PUBLIC_IMAGE_ID = '__public-image'
  const dragEntries = canDrag
    ? compact(
        concat(
          publicImageCard
            ? [{ id: PUBLIC_IMAGE_ID, node: publicImageCard }]
            : [],
          map(sortedCards, (card) => ({
            id: card.name,
            node: card.card,
          })),
        ),
      )
    : []
  const dragIds = map(dragEntries, (entry) => entry.id)

  // Public marketplace has no strategy/workflow sidebars, so 3 columns
  // work from lg. Logged-in dashboard is narrower — wait until xl.
  const cardGridSize = publicDashboard
    ? { xs: 12, md: 6, lg: 4 }
    : { xs: 12, md: 6, xl: 4 }

  return (
    <>
      {!canDrag && (
        <Grid
          container
          direction="row"
          spacing={2}
          sx={{ p: '1rem' }}
        >
          {publicImageCard && (
            <Grid
              size={cardGridSize}

              sx={{ display: 'flex' }}
            >
              {publicImageCard}
            </Grid>
          )}
          {map(
            slice(
              sortedCards,
              0,
              findIndex(
                sortedCards,
                (card) => card.name === 'deactive',
              ),
            ),
            (card) => (
              <Grid
                key={card.name}
                size={cardGridSize}

                sx={{ display: 'flex' }}
              >
                {card.card}
              </Grid>
            ),
          )}
        </Grid>
      )}
      {canDrag && (
        <SortableList
          items={dragIds}
          onSortEnd={onSortEnd}
          strategy="xy"
        >
          <Grid
            container
            direction="row"
            spacing={2}
            sx={{ p: '1rem' }}
          >
            {map(dragEntries, (entry) => (
              <SortableItem
                key={entry.id}
                id={entry.id}
                as={Grid}
                size={cardGridSize}

                sx={{ display: 'flex' }}
              >
                {entry.node}
              </SortableItem>
            ))}
          </Grid>
        </SortableList>
      )}

      {/* Order change drawer */}
      <Drawer
        open={orderChanged}
        anchor="bottom"
        variant="persistent"
        slotProps={{
          paper: { sx: strategyOrderDrawerPaperSx },
        }}
      >
        <Grid
          container
          direction="row"
          spacing={2}
          wrap="nowrap"
          sx={{
            alignItems: 'center',
          }}
        >
          <Grid sx={strategyOrderDrawerCloseWrapSx}>
            <IconButton onClick={resetOrder} size="large">
              <Close sx={strategyOrderCloseIconSx} />
            </IconButton>
          </Grid>
          <Grid sx={strategyOrderResetBtnWrapSx}>
            <Button
              variant="contained"
              size="small"
              sx={strategyOrderSaveBtnSx}
              onClick={submitOrder}
              disabled={loader}
            >
              <Typography sx={strategyOrderSaveLabelSx}>
                Save
              </Typography>
            </Button>
          </Grid>
          <Grid>
            <FormControl
              component="fieldset"
              sx={[strategyOrderFormWhiteSx, { pr: '1.5rem' }]}
            >
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={orderDefault}
                      onChange={() => setOrderDefault(!orderDefault)}
                      sx={strategyOrderFormWhiteSx}
                    />
                  }
                  label={`Set this as default ${strategyName} layout`}
                />
              </FormGroup>
            </FormControl>
          </Grid>
          <Typography sx={strategyOrderFlexSpacerSx} />
        </Grid>
      </Drawer>
    </>
  )
}

export default StrategyPageBase
